// Import Packages
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectInspector, chromeLight } from 'react-inspector';
import { useSetRecoilState } from 'recoil';

// Import Material UI
import CircularProgress from '@material-ui/core/CircularProgress';
import { Dialog, Button, makeStyles, TextField } from '@material-ui/core';

// Import Recoil
import { alertState } from '../../../../../recoil-store/recoil-state';

// Import Icons
import CloseIcon from '../../../../../assets/icons/close-icon.svg';


const useStyles = makeStyles(theme => ({
    dialogContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        background: '#FFFFFF'
    },
    inputRoot: {
        "& fieldset": {
            borderColor: '#E6E6E6',
            borderWidth: '1.1px'
        }
    }
}));

function DataTable({ previewData, outputVariableName, transformKeys, cancel, save }) {
    const [selectedArea, setSelectedArea] = useState({ rowIndex: -5, colIndex: -5 });
    const [manuelRowNumber, setManuelRowNumber] = useState();
    const [manuelColNumber, setManuelColNumber] = useState();
    const [showManuelRange, setShowManuelRange] = useState(false);

    const setAlert = useSetRecoilState(alertState);
    const classes = useStyles();
    const { t } = useTranslation();
    const label = "CONTENT.CONFIGURATION-FORM.ADVANCED-ACTIONS.DATA-TRANSFORM.";


    useEffect(() => {
        setSelectedArea({ rowIndex: -5, colIndex: -5 });
    }, [previewData, transformKeys])

    const selectRow = (index) => {
        let data = { rowIndex: index, colIndex: -1 };
        if (!(selectedArea.rowIndex === index && selectedArea.colIndex === -1)) {
            setSelectedArea(data);
        }
    }

    const selectCell = (rowIndex, colIndex) => {
        let data = { rowIndex, colIndex };
        if (!(selectedArea.rowIndex === rowIndex && selectedArea.colIndex === colIndex)) {
            setSelectedArea(data);
        }
    }

    const selectColumn = (index) => {
        let data = { rowIndex: -1, colIndex: index };
        if (!(selectedArea.rowIndex === -1 && selectedArea.colIndex === index)) {
            setSelectedArea(data);
        }
    }

    const renderDataTable = () => {
        if (!previewData.data) {
            return undefined
        }

        let v = previewData.data;
        if (!Array.isArray(previewData.data) && previewData.data[outputVariableName]) {
            v = previewData.data[outputVariableName];
        }


        return v.map((row, index) => {
            const backgroundColor = index === 0 ? '#4F6E90' : '#F4F4F4';
            const color = index === 0 ? '#F8F8F8' : '#4A4A4A';


            let tdStyle = {};
            if (selectedArea.rowIndex === index && selectedArea.colIndex === -1) {
                tdStyle = { backgroundColor: "#43AA4163" };
            } else {
                tdStyle = { backgroundColor: "#F4F4F4" };
            }

            return (
                <tr style={{ backgroundColor, color }} key={index} >
                    {/* <td onClick={() => selectRow(index)} style={tdStyle} className='data-table-info-td data-table-row-info'>{`R${index + 1}`}</td> Sıra numarası ekleniyor */}
                    {row.map((cell, cellIndex) => {
                        let tdStyle = {};
                        if ((selectedArea.rowIndex === index || selectedArea.rowIndex === -1) && (selectedArea.colIndex === -1 || selectedArea.colIndex === cellIndex)) {
                            tdStyle = { backgroundColor: "#43AA4163", color };
                        }

                        return <td style={tdStyle} className={`data-table-tbody-td ${index === 0 ? "data-table-first-header-td" : ""}`} key={cellIndex}
                            onClick={() => selectCell(index, cellIndex)}>
                            {cell}
                        </td>;
                    })}
                </tr>
            );
        });
    };

    const headerCols = () => {
        if (!previewData.data) {
            return undefined
        }
        let v = previewData.data;
        if (!Array.isArray(previewData.data) && previewData.data[outputVariableName]) {
            v = previewData.data[outputVariableName];
        }

        const columnHeaders = Object.keys(v[0]);
        return (
            <thead>
                <tr>
                    <td></td>
                    {
                        columnHeaders.map((row, index) => {
                            let tdStyle = {};
                            if (selectedArea.rowIndex === -1 && selectedArea.colIndex === index) {
                                tdStyle = { backgroundColor: "#43AA4163" };
                            }

                            return <td style={tdStyle} className='data-table-header-col-name data-table-info-td' key={index}
                                onClick={() => selectColumn(index)}>
                                C{index + 1}
                            </td>
                        })
                    }
                </tr>
            </thead>
        );
    }

    const showRange = (rowIndex, colIndex) => {
        const pattern = /\[(\d+)\]/g;
        let c = transformKeys && transformKeys.length > 0 ? <span className='select-area-range-value'>{" ; "}</span> : "";
        //  // check if the source itself has been converted
        if (c && transformKeys.length === 1 && !pattern.test(transformKeys[0])) {
            c = "";
        }
        if (rowIndex > -1 && colIndex > -1) {
            let path = `${outputVariableName}[${rowIndex}][${colIndex}]`;
            if (!transformKeys.find(x => x === path)) {
                return <span className='select-area-range-value'>
                    {c}
                    <span className='range-bold icon-cursor-pointer'>({`R${rowIndex + 1},C${colIndex + 1}`})</span>
                </span>
            }
        } else if (!c) {
            return (
                <span className='select-area-no-content'>{t(label + "SELECT-VALUE")}</span>
            )
        }
    }


    const getRange = (path, i) => {
        const pattern = /\[(\d+)\]/g;
        let numbers = [];
        let match;
        while ((match = pattern.exec(path)) !== null) {
            numbers.push(parseInt(match[1], 10));
        }
        if (numbers.length === 2) {
            let cls = "icon-cursor-pointer";
            if (selectedArea && selectedArea.rowIndex === numbers[0] && selectedArea.colIndex === numbers[1]) {
                cls = "icon-cursor-pointer range-bold"
            }
            let seperator = "";
            if (i !== 0) {
                seperator = " ; ";
                // check if the source itself has been converted
                if (i === 1 && !pattern.test(transformKeys[0])) {
                    seperator = "";
                }
            }
            return <span className="select-area-range-value">
                {seperator}
                <span onClick={() => setSelectedArea({ rowIndex: numbers[0], colIndex: numbers[1] })} className={cls}>
                    ({`R${numbers[0] + 1},C${numbers[1] + 1}`})
                </span>
            </span>
        } else {
            return "";
        }
    }

    const selectManuelRange = () => {

        if (!manuelRowNumber || !manuelColNumber) {
            return setAlert(alert => ({
                ...alert,
                show: true,
                message: t('ACTIVE-DIRECTORY.NOT-NULL'),
                severity: 'error'
            }))
        }

        setSelectedArea({ rowIndex: manuelRowNumber - 1, colIndex: manuelColNumber - 1 });
        setManuelColNumber(undefined);
        setManuelRowNumber(undefined);
        setShowManuelRange(false);
    }

    const selectValue = () => {
        if (selectedArea.rowIndex < 0 || selectedArea.colIndex < 0) {
            return setAlert(alert => ({
                ...alert,
                show: true,
                message: t(label + 'CELL-NOT-FOUND'),
                severity: 'error'
            }))
        }
        let path = `${outputVariableName}[${selectedArea.rowIndex}][${selectedArea.colIndex}]`
        save(path);
    }

    return (
        <Dialog open={true} className='base-dialog-content' maxWidth={850} PaperProps={{ className: classes.dialogContainer }}>
            <div className='data-table-select-container'>

                <div className='items-center-between'>
                    <div className='base-modal-title base-title-detail'>
                        {t(label + "SELECT-VALUE")}
                    </div>
                    <img className='transform-cancel-icon' src={CloseIcon} onClick={cancel} />
                </div>
                <div className='transform-sub-description'>
                    {t(label + "SELECT-VALUES-DESC")}
                </div>

                <div className='data-table-select-area'>
                    {transformKeys &&
                        transformKeys.slice(0, Math.min(15, transformKeys.length)).map((path, i) =>
                            getRange(path, i)
                        )
                    }

                    {selectedArea ? showRange(selectedArea.rowIndex, selectedArea.colIndex) : ""}

                    {transformKeys && transformKeys.length > 15 &&
                        <span className='icon-cursor-pointer' onClick={() => setShowManuelRange(true)}>{"    ... "}</span>
                    }
                </div>
                <div className='transform-table-container table-layout-auto'>
                    <div className='manuel-range-container'>
                        <Button className='manuel-range-button' onClick={() => setShowManuelRange(true)}>
                            {t(label + 'SELECT-MANUEL-RANGE')}
                        </Button>
                    </div>
                    <div className='data-table-table-container'>
                        <table className='transform-data-table-container table-layout-auto' >

                            {/* {headerCols()} */}
                            <tbody>
                                {renderDataTable()}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className='configuration-footer'>
                    <Button className='transform-submit-button m-w-140' onClick={selectValue}>
                        {t(label + 'SELECT-VALUE')}
                    </Button>
                </div>

                {/* Manuel Range Modal Start */}
                {
                    showManuelRange &&
                    <Dialog open={true} className='base-dialog-content' PaperProps={{ className: classes.dialogContainer }}>
                        <div className='manuel-range-modal-container'>
                            <div className='items-center-between'>
                                <div className='base-modal-title base-title-detail'>
                                    {t(label + "SELECT-MANUEL-RANGE")}
                                </div>
                                <img className='transform-cancel-icon' src={CloseIcon} onClick={() => setShowManuelRange(false)} />
                            </div>
                            <div className='m-t-20'>
                                <TextField
                                    type='number'
                                    value={manuelRowNumber}
                                    className="select-action-input"
                                    onChange={(v) => {
                                        if (parseInt(v.target.value) > 0) {
                                            setManuelRowNumber(v.target.value)
                                        }
                                    }}
                                    placeholder={t(label + "ROW-NUMBER")}
                                    variant="outlined"
                                    classes={{
                                        root: classes.inputRoot
                                    }}
                                    inputProps={{
                                        style: {
                                            marginLeft: '5px',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: '#46474E'
                                        }
                                    }}
                                />
                            </div>
                            <div className='m-t-10'>
                                <TextField
                                    type='number'
                                    value={manuelColNumber}
                                    onChange={(v) => {
                                        if (parseInt(v.target.value) > 0) {
                                            setManuelColNumber(v.target.value)
                                        }
                                    }}
                                    className="select-action-input"
                                    placeholder={t(label + "COL-NUMBER")}
                                    variant="outlined"
                                    classes={{
                                        root: classes.inputRoot
                                    }}
                                    inputProps={{
                                        style: {
                                            marginLeft: '5px',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: '#46474E'
                                        }
                                    }}
                                />
                            </div>

                            <div className='configuration-footer'>
                                <Button className='transform-submit-button m-w-140' onClick={selectManuelRange}>
                                    {t(label + 'SELECT')}
                                </Button>
                            </div>
                        </div>

                    </Dialog >
                }
                {/* Manuel Range Modal End */}

            </div >
        </Dialog >
    )
}

export default DataTable;